package dev.moetz.chatoverlay.page

import csstype.PropertiesBuilder
import dev.moetz.chatoverlay.chatclient.ChatClient
import dev.moetz.chatoverlay.chatclient.PreviewChatClient
import dev.moetz.chatoverlay.chatclient.TwitchChatClient
import dev.moetz.chatoverlay.localization.Localization
import dev.moetz.chatoverlay.manager.EmoteOrText
import dev.moetz.chatoverlay.manager.TextToThirdPartyEmoteManager
import dev.moetz.chatoverlay.model.BroadcasterId
import dev.moetz.chatoverlay.model.ChatMessage
import dev.moetz.chatoverlay.model.IncomingIRC
import dev.moetz.chatoverlay.model.thirdpartyemote.EmoteProviderReloadEvent
import dev.moetz.chatoverlay.model.thirdpartyemote.ThirdPartyEmote
import dev.moetz.chatoverlay.model.twitch.BadgeSet
import dev.moetz.chatoverlay.model.twitch.Cheermote
import dev.moetz.reconnectingwebsocket.ReconnectingWebSocketClient
import dev.moetz.websocket.page.client.useAsState
import emotion.react.css
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.i
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import web.cssom.*
import web.dom.document
import web.url.URLSearchParams
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

external interface ChatOverlayServiceProps : Props {
    var manager: ChatOverlayServiceManager
}


@OptIn(ExperimentalStdlibApi::class, ExperimentalWasmJsInterop::class)
class ChatOverlayServiceManager(
    scope: CoroutineScope = GlobalScope,
    private val textToThirdPartyEmoteManager: TextToThirdPartyEmoteManager = TextToThirdPartyEmoteManager(),
) {

    private val json = Json

    private val mutableGlobalBadgeSetsStateFlow: MutableStateFlow<List<BadgeSet>> = MutableStateFlow(emptyList())

    private val mutableChannelBadgeSetStateFlow: MutableStateFlow<Map<BroadcasterId, List<BadgeSet>>> =
        MutableStateFlow(emptyMap())
    private val channelBadgeSetMutex = Mutex()

    private val mutableGlobalThirdPartyEmotesStateFlow: MutableStateFlow<List<ThirdPartyEmote>> =
        MutableStateFlow(emptyList())

    private val mutableChannelThirdPartyEmotesStateFlow: MutableStateFlow<Map<BroadcasterId, List<ThirdPartyEmote>>> =
        MutableStateFlow(emptyMap())

    private val channelThirdPartyEmotesMutex = Mutex()

    private val mutableCheermotesStateFlow: MutableStateFlow<Map<BroadcasterId, List<Cheermote>>> =
        MutableStateFlow(emptyMap())

    private val cheermotesMutex = Mutex()

    val fontColor: String
    val fontSize: Int
    val shadowColor: String?
    val shadowBlurRadius: Double
    val backgroundColor: Triple<Int, Int, Int>?
    val backgroundAlpha: Double
    val showChannelProfileImage: Boolean
    private val hiddenUsernames: List<String>
    private val hideCommands: Boolean
    private val loadRecentMessages: Boolean
    val disableSharedChatMessages: Boolean
    val showPredictionBadges: Boolean
    val showSubscriberBadges: Boolean
    val showModerationBadges: Boolean
    private val messageDelay: Duration?

    private val isPreview: Boolean
    private val loggingEnabled: Boolean

    val twitchChatClient: ChatClient

    private val mutableChatMessagesStateFlow: MutableStateFlow<List<ChatMessage>> =
        MutableStateFlow(emptyList())
    val chatMessagesStateFlow = mutableChatMessagesStateFlow.asStateFlow()

    private val mutableIsSharedCharActiveStateFlow: MutableStateFlow<Boolean> = MutableStateFlow(false)
    val isSharedCharActiveStateFlow = mutableIsSharedCharActiveStateFlow.asStateFlow()

    private val combinedEmoteProviderUpdateEventsWebSocket: ReconnectingWebSocketClient

    init {
        val urlSearchParams = URLSearchParams(window.location.search)
        isPreview = urlSearchParams.get("preview")?.toBoolean() ?: false
        loggingEnabled = urlSearchParams.get("loggingEnabled")?.toBoolean() ?: false

        val channels = urlSearchParams.get("channels")?.split(",").orEmpty().map { it.lowercase() }
        fontColor = urlSearchParams.get("fontColor")?.filter { it.isLetterOrDigit() || it == '#' } ?: "black"
        fontSize = urlSearchParams.get("fontSize")?.toIntOrNull() ?: 24
        shadowColor = urlSearchParams.get("shadowColor")?.filter { it.isLetterOrDigit() || it == '#' }
        shadowBlurRadius = urlSearchParams.get("shadowBlurRadius")?.toDoubleOrNull() ?: 5.0
        backgroundColor =
            urlSearchParams.get("backgroundColor")?.filter { it.isLetterOrDigit() || it == '#' }?.let { hexString ->
                try {
                    val rHex = "${hexString.getOrNull(1)}${hexString.getOrNull(2)}".hexToInt()
                    val gHex = "${hexString.getOrNull(3)}${hexString.getOrNull(4)}".hexToInt()
                    val bHex = "${hexString.getOrNull(5)}${hexString.getOrNull(6)}".hexToInt()
                    Triple(rHex, gHex, bHex)
                } catch (throwable: Throwable) {
                    null
                }
            }
        backgroundAlpha = urlSearchParams.get("backgroundAlpha")?.toDoubleOrNull() ?: 1.0
        showChannelProfileImage = urlSearchParams.get("showChannelProfileImage")?.toBoolean() ?: true
        hiddenUsernames = urlSearchParams.get("hiddenUsernames")?.split(",")?.map { it.trim().lowercase() }.orEmpty()
        hideCommands = urlSearchParams.get("hideCommands")?.toBoolean() ?: false
        loadRecentMessages = urlSearchParams.get("loadRecentMessages")?.toBoolean() ?: true
        disableSharedChatMessages = urlSearchParams.get("disableSharedChatMessages")?.toBoolean() ?: false
        showPredictionBadges = urlSearchParams.get("showPredictionBadges")?.toBoolean() ?: true
        showSubscriberBadges = urlSearchParams.get("showSubscriberBadges")?.toBoolean() ?: true
        showModerationBadges = urlSearchParams.get("showModerationBadges")?.toBoolean() ?: true
        messageDelay = urlSearchParams.get("messageDelay")?.toIntOrNull()?.seconds

        val messageAllowedPredicate: (IncomingIRC.Message) -> Boolean = { message ->
            val disallowedDueToWrongRoom = disableSharedChatMessages && message.isFromDifferentRoom
            val allowedAccordingToCommand = hideCommands.not() || message.message.startsWith("!").not()
            val allowedAccordingToDisplayName =
                (message.displayName.orEmpty().lowercase() in hiddenUsernames).not()

            allowedAccordingToCommand && allowedAccordingToDisplayName && disallowedDueToWrongRoom.not()
        }

        twitchChatClient = if (isPreview) {
            PreviewChatClient(
                apiBaseUrl = window.location.href,
                loggingEnabled = loggingEnabled,
                messageAllowedPredicate = messageAllowedPredicate
            )
        } else {
            TwitchChatClient(
                apiBaseUrl = window.location.href,
                messageShowCount = 50,
                messageAllowedPredicate = messageAllowedPredicate,
                loggingEnabled = loggingEnabled,
                messageDelay = messageDelay,
            )
        }

        combinedEmoteProviderUpdateEventsWebSocket = ReconnectingWebSocketClient(
            url = getFullWebsocketUrlForPath("api/combined/updates?channels=${channels.joinToString(separator = ",")}"),
            startToRetryInstantly = false,
            debugOutput = loggingEnabled,
        )

        data class MetaProvider(
            val globalThirdPartyEmotes: List<ThirdPartyEmote>,
            val channelThirdPartyEmotes: Map<BroadcasterId, List<ThirdPartyEmote>>,
            val cheermotes: Map<BroadcasterId, List<Cheermote>>,
            val mutableChannelBadgeSet: Map<BroadcasterId, List<BadgeSet>>,
            val globalBadgeSets: List<BadgeSet>,
        )

        twitchChatClient.seenRoomIdsFlow
            .distinctUntilChanged()
            .onEach { broadcasterIds ->
                broadcasterIds.forEach { broadcasterId ->
                    twitchChatClient.getChannelEmotesByBroadcasterId(broadcasterId)?.also {
                        channelBadgeSetMutex.withLock {
                            mutableChannelBadgeSetStateFlow.value = mutableChannelBadgeSetStateFlow.value
                                .toMutableMap()
                                .apply {
                                    this[broadcasterId] = it
                                }

                        }
                    }
                }

                broadcasterIds.forEach { broadcasterId ->
                    loadThirdPartyEmotesForChannel(broadcasterId = broadcasterId)
                }
                broadcasterIds.forEach { broadcasterId ->
                    twitchChatClient.getCheermotes(broadcasterId)?.also {
                        cheermotesMutex.withLock {
                            mutableCheermotesStateFlow.value =
                                mutableCheermotesStateFlow.value
                                    .toMutableMap()
                                    .apply {
                                        this[broadcasterId] = it
                                    }
                        }
                    }
                }
            }
            .launchIn(scope)

        scope.launch {
            twitchChatClient.connectAndJoinChannels(channels, loadRecentMessages)

            this.launch {
                twitchChatClient.getGlobalBadges()?.also {
                    mutableGlobalBadgeSetsStateFlow.emit(it)
                }
            }

            this.launch {
                twitchChatClient.getCombinedGlobalEmotes()?.also {
                    mutableGlobalThirdPartyEmotesStateFlow.emit(it)
                }
            }

            combinedEmoteProviderUpdateEventsWebSocket.received
                .mapNotNull { serialized ->
                    try {
                        json.decodeFromString(EmoteProviderReloadEvent.serializer(), serialized)
                    } catch (throwable: Throwable) {
                        throwable.printStackTrace()
                        null
                    }
                }
                .onEach { event ->
                    loadThirdPartyEmotesForChannel(
                        broadcasterId = event.broadcasterId
                    )
                }
                .flowOn(Dispatchers.Default)
                .launchIn(this)

            combinedEmoteProviderUpdateEventsWebSocket.connect()

        }

        scope.launch {
            combine(
                twitchChatClient.messagesStateFlow,
                combine(
                    mutableGlobalThirdPartyEmotesStateFlow,
                    mutableChannelThirdPartyEmotesStateFlow,
                    mutableCheermotesStateFlow,
                    mutableChannelBadgeSetStateFlow,
                    mutableGlobalBadgeSetsStateFlow,
                ) { globalThirdPartyEmotes, channelThirdPartyEmotes, cheermotes, mutableChannelBadgeSet, globalBadgeSets ->
                    MetaProvider(
                        globalThirdPartyEmotes = globalThirdPartyEmotes,
                        channelThirdPartyEmotes = channelThirdPartyEmotes,
                        cheermotes = cheermotes,
                        mutableChannelBadgeSet = mutableChannelBadgeSet,
                        globalBadgeSets = globalBadgeSets,
                    )
                }
            ) { messages, metaProvider -> Pair(messages, metaProvider) }
                .map { (messages, metaProvider) ->
                    messages
                        .map { message ->
                            val channelEmotes =
                                metaProvider.channelThirdPartyEmotes[message.actualRoomId?.let { BroadcasterId(it) }].orEmpty()
                            val emotes = metaProvider.globalThirdPartyEmotes + channelEmotes

                            val emotesCleanedUpWithZeroWidth = textToThirdPartyEmoteManager.checkZeroWidthEmotes(
                                textToThirdPartyEmoteManager.parseThirdPartyEmotes(
                                    message,
                                    emotes = emotes,
                                    cheermotes = metaProvider.cheermotes[message.actualRoomId?.let { BroadcasterId(it) }].orEmpty(),
                                )
                            )

                            Pair(message, emotesCleanedUpWithZeroWidth)
                        }
                        .mapNotNull { (message, fragments) ->
                            message.toChatMessage(
                                fragments = fragments,
                                channelBadgeSet = metaProvider.mutableChannelBadgeSet,
                                globalBadgeSets = metaProvider.globalBadgeSets,
                            )
                        }
                }
                .distinctUntilChanged()
                .onEach { list -> mutableChatMessagesStateFlow.value = list }
                .launchIn(this)
        }

        twitchChatClient.messagesStateFlow
            .mapNotNull { messages -> messages.lastOrNull() }
            .map { message -> message.sourceRoomId != null }
            .distinctUntilChanged()
            .onEach { isInSharedRoom ->
                mutableIsSharedCharActiveStateFlow.value = isInSharedRoom
            }
            .launchIn(scope)
    }

    private fun getBadgeUrl(
        broadcasterId: BroadcasterId?,
        badgeSetNameWithNumber: IncomingIRC.BadgeSetNameWithNumber,
        channelBadgeSet: Map<BroadcasterId, List<BadgeSet>>,
        globalBadgeSets: List<BadgeSet>,
    ): String? {
        return when (badgeSetNameWithNumber.set) {
            "subscriber" -> {
                channelBadgeSet[broadcasterId]
                    ?.firstOrNull { it.setId == "subscriber" }
                    ?.versions
                    ?.firstOrNull { it.id == badgeSetNameWithNumber.value }
                    ?.imageUrl4x
            }

            "bits" -> {
                channelBadgeSet[broadcasterId]
                    ?.firstOrNull { it.setId == "bits" }
                    ?.versions
                    ?.firstOrNull { it.id == badgeSetNameWithNumber.value }
                    ?.imageUrl4x
            }

            else -> {
                globalBadgeSets
                    .firstOrNull { it.setId == badgeSetNameWithNumber.set }
                    ?.versions
                    ?.firstOrNull { it.id == badgeSetNameWithNumber.value }
                    ?.imageUrl4x
            }
        }
    }

    private suspend fun loadThirdPartyEmotesForChannel(broadcasterId: BroadcasterId) {
        twitchChatClient.getCombinedChannelEmotes(broadcasterId)?.also {
            channelThirdPartyEmotesMutex.withLock {
                mutableChannelThirdPartyEmotesStateFlow.value =
                    mutableChannelThirdPartyEmotesStateFlow.value
                        .toMutableMap()
                        .apply {
                            this[broadcasterId] = it
                        }
            }
        }
    }

    private fun getFullWebsocketUrlForPath(path: String): String {
        return buildString {
            val hostname = window.location.hostname
            val port = window.location.port
            val isSecure = window.location.protocol == "https:"
            if (isSecure) {
                append("wss://")
            } else {
                append("ws://")
            }
            append(hostname)
            if (port.isBlank().not()) {
                append(':')
                append(port)
            }
            if (path.startsWith('/').not()) {
                append('/')
            }
            append(path)
        }
    }

    private fun IncomingIRC.Message.toChatMessage(
        fragments: List<EmoteOrText>,
        channelBadgeSet: Map<BroadcasterId, List<BadgeSet>>,
        globalBadgeSets: List<BadgeSet>,
    ): ChatMessage? {
        return if (this.actualRoomId != null && this.displayName != null) {
            ChatMessage(
                id = this.id,
                roomId = this.actualRoomId!!,
                roomProfileImageUrl = "/twitch/${this.actualRoomId}/profileImage.png",
                badges = this.badges.mapNotNull { badge ->
                    val url = getBadgeUrl(
                        broadcasterId = this.actualRoomId?.let { BroadcasterId(it) },
                        badgeSetNameWithNumber = badge,
                        channelBadgeSet = channelBadgeSet,
                        globalBadgeSets = globalBadgeSets,
                    )
                    if (url != null) {
                        ChatMessage.Badge(
                            url = url,
                            set = badge.set,
                            value = badge.value,
                        )
                    } else {
                        null
                    }
                },
                color = this.color,
                displayName = this.displayName,
                isSlashMe = this.isSlashMe,
                messageFragments = fragments,
            )
        } else {
            null
        }
    }

}


val ChatOverlayService = FC<ChatOverlayServiceProps> { props ->
    val connected = props.manager.twitchChatClient.connectedFlow.useAsState()
    val chatMessages = props.manager.chatMessagesStateFlow.useAsState()
    val isSharedChatActive = props.manager.isSharedCharActiveStateFlow.useAsState()
    val fontColor = props.manager.fontColor
    val fontSize = props.manager.fontSize
    val shadowColor = props.manager.shadowColor
    val shadowBlurRadius = props.manager.shadowBlurRadius
    val backgroundColor = props.manager.backgroundColor
    val backgroundAlpha = props.manager.backgroundAlpha
    val showChannelProfileImage =
        props.manager.showChannelProfileImage || (props.manager.disableSharedChatMessages.not() && isSharedChatActive)

    div {
        css {
            if (backgroundColor != null) {
                this.backgroundColor = rgb(
                    backgroundColor.first,
                    backgroundColor.second,
                    backgroundColor.third,
                    backgroundAlpha
                )
            }
        }

        if (connected.not()) {
            span {
                css {
                    color = Color("red")
                }
                +Localization["overlay_loading"]
            }
        }

        div {
            css {
                this.fontSize = fontSize.px
                padding = 6.px
                fontFamily = string("Roboto")
                fontWeight = integer(500)
                display = Display.flex
                flexDirection = FlexDirection.columnReverse
            }
            chatMessages.reversed().forEach { chatMessage ->
                div {
                    css {
                        color = Color(fontColor)
                        if (shadowColor != null) {
                            textShadow = TextShadow(0.px, 0.px, shadowBlurRadius.px, Color(shadowColor))
                        }
                    }

                    // channel image
                    if (showChannelProfileImage) {
                        image(
                            url = chatMessage.roomProfileImageUrl,
                            key = "${chatMessage.id}_${chatMessage.roomId}_profileImage",
                            height = 0.75.em,
                            marginRight = 6.px,
                            borderRadius = 50.pct,
                        )
                    }

                    // badges
                    chatMessage.badges
                        .asSequence()
                        .filter { props.manager.showPredictionBadges || it.isPredictionBadge.not() }
                        .filter { props.manager.showModerationBadges || it.isModeratorBadge.not() }
                        .filter { props.manager.showSubscriberBadges || it.isSubscriberBadge.not() }
                        .forEach { badge ->
                            image(
                                url = badge.url,
                                key = "${chatMessage.id}_${chatMessage.roomId}_badge_${badge.value}_${badge.set}",
                                height = 0.75.em,
                                marginRight = 3.px,
                                marginLeft = 3.px,
                            )
                        }

                    // username
                    span {
                        css {
                            chatMessage.color?.also {
                                color = Color(it)
                            }
                            marginLeft = 3.px
                            fontWeight = integer(800)
                        }
                        +"${chatMessage.displayName}: "
                    }

                    if (chatMessage.isSlashMe) {
                        i {
                            showMessageWithEmotes(chatMessage)
                        }
                    } else {
                        showMessageWithEmotes(chatMessage)
                    }


                    onLoad = {
                        window.scrollTo(0.0, document.body.scrollHeight.toDouble())
                    }
                }
            }
        }
    }
}

fun ignoreCaseOpt(ignoreCase: Boolean) =
    if (ignoreCase) setOf(RegexOption.IGNORE_CASE) else emptySet()

fun String.indexesOf(pat: String, ignoreCase: Boolean = true): List<Int> {
    return try {
        pat.toRegex(ignoreCaseOpt(ignoreCase))
            .findAll(this)
            .map { it.range.first }
            .toList()
    } catch (throwable: Throwable) {
        println("Error regexing '$pat'")
        throwable.printStackTrace()
        emptyList()
    }
}


private fun ChildrenBuilder.showMessageWithEmotes(
    message: ChatMessage,
) {
    message.messageFragments.forEachIndexed { index, emoteOrText ->
        when (emoteOrText) {
            is EmoteOrText.TwitchEmote, is EmoteOrText.ParsedEmote -> {
                val (url, label) = when (emoteOrText) {
                    is EmoteOrText.TwitchEmote -> {
                        Pair(
                            "https://static-cdn.jtvnw.net/emoticons/v2/${emoteOrText.emoteWithLocation.emoteId}/default/light/3.0",
                            emoteOrText.emoteWithLocation.name,
                        )
                    }

                    is EmoteOrText.ParsedEmote -> {
                        Pair(
                            emoteOrText.emote.url,
                            emoteOrText.emote.name,
                        )
                    }

                    else -> {
                        Pair(null, null)
                    }
                }

                if (emoteOrText.zeroWidthEmoteOverlays.isNotEmpty()) {
                    span {
                        css {
                            position = Position.relative
                            display = Display.inlineFlex
                            justifyContent = JustifyContent.center
                            flexDirection = FlexDirection.row
                            verticalAlign = VerticalAlign.textTop
                        }

                        val labelWithZeroWidths =
                            (listOf(label) + emoteOrText.zeroWidthEmoteOverlays.map { it.name }).joinToString(separator = ", ")

                        span {
                            image(
                                url = url,
                                key = "${message.id}_${index}",
                                height = 1.em,
                                marginLeft = 0.px,
                                marginRight = 0.px,
                                title = labelWithZeroWidths,
                            )
                        }
                        emoteOrText.zeroWidthEmoteOverlays.forEachIndexed { zeroWidthIndex, zeroWidthEmote ->
                            span {
                                css {
                                    position = Position.absolute
                                }
                                image(
                                    url = zeroWidthEmote.url,
                                    key = "${message.id}_${index}_${zeroWidthIndex}",
                                    height = 1.em,
                                    marginLeft = 0.px,
                                    marginRight = 0.px,
                                    title = labelWithZeroWidths,
                                )
                            }
                        }
                    }
                } else {
                    image(
                        url = url,
                        key = "${message.id}_${index}",
                        height = 1.em,
                        marginLeft = 0.px,
                        marginRight = 0.px,
                        title = label,
                        additionalCss = {
                            verticalAlign = VerticalAlign.textTop
                        }
                    )
                }
            }

            is EmoteOrText.Cheer -> {
                +"${emoteOrText.amount} "

                val url = emoteOrText.emote.getTierForBits(emoteOrText.amount)?.images?.light?.getImageUrl()
                image(
                    url = url,
                    key = "${message.id}_${index}",
                    title = "${emoteOrText.emote.prefix} (${emoteOrText.amount} bits)",
                    height = 0.85.em,
                    marginLeft = 0.px,
                    marginRight = 0.px,
                )
            }

            is EmoteOrText.Text -> {
                span {
                    +emoteOrText.text
                }
            }
        }
    }
}

fun ChildrenBuilder.image(
    url: String?,
    key: String,
    title: String? = null,
    height: Length = 0.85.em,
    marginLeft: Length = 2.px,
    marginRight: Length = 2.px,
    borderRadius: Length? = null,
    additionalCss: (PropertiesBuilder.() -> Unit)? = null,
) {
    img {
        css {
            this.height = height
            this.marginLeft = marginLeft
            this.marginRight = marginRight

            // Wanted to use minWidth here to _reserve_ the space when the emote is still loading, however this made
            // issues with smaller emotes (e.g. FeelsBirthdayMan)
//            this.minWidth = 0.8.em
            if (borderRadius != null) {
                this.borderRadius = borderRadius
            }
            additionalCss?.invoke(this)
        }
        this.title = title
        src = url
        this.key = key
    }
}
